import React, { useEffect, useState } from 'react'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Select from '@react/react-spectrum/Select'
import { Table, TBody, TD, TH, THead, TR } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'

import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Help from '@react/react-spectrum/Icon/Help'

import './FormEditor.scss'

const FIELD_TYPES = [
  {
    label: 'Text',
    value: 'text',
  },
  {
    label: 'Number',
    value: 'number',
  },
  {
    label: 'Checkbox',
    value: 'checkbox',
  },
  {
    label: 'Calendar',
    value: 'calendar',
  },
  {
    label: 'Dropdown',
    value: 'dropdown',
  },
]

const NEW_FIELD = {
  type: FIELD_TYPES[0].value,
}

export const FormEditor = ({ fields, disabled, onChange }) => {
  const [rows, setRows] = useState([])
  const hasEmptyRow = rows.length > 0 && rows.filter(r => !r.name).length > 0

  const saveField = (index, propName) => value => {
    let _value = propName === 'name' ? value.replace(/(^[0-9]+|-|\s)/gm, '') : value
    const newRows = rows.map((r, idx) => (idx === index ? { ...r, [propName]: _value } : r))
    setRows(newRows)
    onChange(newRows)
  }

  const addRow = () => {
    if (!hasEmptyRow) {
      const newRows = [...rows, NEW_FIELD]
      setRows(newRows)
    }
  }

  const deleteRow = index => {
    const newRows = rows.filter((r, idx) => idx !== index)
    setRows(newRows)
    onChange(newRows)
  }

  useEffect(() => {
    setRows(fields || [])
  }, [fields])

  return (
    <div className='formEditor'>
      <Table onSubmit={evt => evt.preventDefault()}>
        <THead>
          <TH>Label</TH>
          <TH>Name</TH>
          <TH>Type</TH>
          <TH>
            Options{' '}
            <OverlayTrigger placement='right'>
              <Button className='tooltip-btn' variant='tool' icon={<Help />} />
              <Tooltip>Comma-separated list of dropdown options.</Tooltip>
            </OverlayTrigger>
          </TH>
          <TH></TH>
        </THead>
        <TBody>
          {rows.map((field, idx) => (
            <TR key={'field-' + idx}>
              <TD>
                <Textfield value={field.label} onChange={saveField(idx, 'label')} disabled={disabled} />
              </TD>
              <TD>
                <Textfield value={field.name} onChange={saveField(idx, 'name')} disabled={disabled} />
              </TD>
              <TD>
                <Select
                  value={field.type}
                  options={FIELD_TYPES}
                  onSelect={saveField(idx, 'type')}
                  disabled={disabled}
                />
              </TD>
              <TD>
                <Textfield
                  value={field.options}
                  onChange={saveField(idx, 'options')}
                  disabled={disabled || field.type !== 'dropdown'}
                />
              </TD>
              <TD>
                <Button
                  variant='tool'
                  aria-label='Delete'
                  icon={<Delete />}
                  onClick={() => deleteRow(idx)}
                  disabled={disabled}
                />
              </TD>
            </TR>
          ))}
          <TR onClick={addRow}>
            <TD>
              <Button variant='action' quiet label='+ Add' disabled={disabled || hasEmptyRow} />
            </TD>
            <TD />
            <TD />
            <TD />
            <TD />
          </TR>
        </TBody>
      </Table>
    </div>
  )
}

export const FormEditorDialog = ({ label, fields, onChange, ...otherProps }) => {
  const [newContent, setNewContent] = useState(fields || [])
  return (
    <Dialog
      {...otherProps}
      className={'form-editor--dialog'}
      title={label || 'Form Editor'}
      confirmLabel='Save'
      cancelLabel='Discard'
      onConfirm={() => onChange(newContent)}
      role='dialog'
    >
      <FormEditor fields={newContent} onChange={setNewContent} />
    </Dialog>
  )
}

export const FormEditorInput = ({ label, fields, disabled, onChange }) => (
  <ModalTrigger>
    <Button icon={<Edit />}>Open Editor</Button>
    <FormEditorDialog label={label} fields={fields} onChange={onChange} disabled={disabled} />
  </ModalTrigger>
)
